import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Link2, Copy, Send, Mail, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const ROLE_LABEL = { manager: "Manager", staff: "Staff" };
const STATUS_COLOR = {
    active: "text-[#00e676]",
    accepted: "text-[#00e676]",
    pending: "text-[#ffea00]",
    sent: "text-[#00e676]",
    failed: "text-[#ff3333]",
    revoked: "text-[#a3a3a3]",
    expired: "text-[#a3a3a3]",
};
const EXPIRY = [{ value: 24, label: "24h" }, { value: 72, label: "3 days" }, { value: 168, label: "7 days" }];
const CHANNELS = [
    { value: "", label: "—" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "sms", label: "SMS" },
    { value: "instagram_dm", label: "Instagram DM" },
    { value: "email", label: "Email" },
    { value: "in_person", label: "In person" },
    { value: "other", label: "Other" },
];
const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map((c) => [c.value, c.label]));

export default function VenueTeam() {
    const { venue } = useOutletContext();
    const [team, setTeam] = useState([]);
    const [invites, setInvites] = useState([]);
    const [summary, setSummary] = useState(null);
    const [emailOpen, setEmailOpen] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);

    const load = () => {
        endpoints.venueTeam(venue.id).then((d) => setTeam(d.team));
        endpoints.venueListInvitations(venue.id).then((d) => setInvites(d.invitations));
        endpoints.venueShareLinkSummary(venue.id).then(setSummary).catch(() => setSummary(null));
    };
    useEffect(load, [venue.id]);

    const removeMember = async (uid) => {
        if (!window.confirm("Remove this member from the venue?")) return;
        try { await endpoints.venueRemoveMember(venue.id, uid); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const revoke = async (inv) => {
        const label = inv.kind === "share_link" ? "this share link" : `the invitation for ${inv.email}`;
        if (!window.confirm(`Revoke ${label}?`)) return;
        try { await endpoints.venueRevokeInvite(venue.id, inv.id); toast.success("Revoked"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const copyLink = (inv) => {
        const url = `${window.location.origin}/accept-invite?token=${inv.token}`;
        navigator.clipboard.writeText(url); toast.success("Link copied");
    };

    const emailInvites = invites.filter((i) => (i.kind || "email") === "email");
    const linkInvites = invites.filter((i) => i.kind === "share_link");
    const pendingEmail = emailInvites.filter((i) => i.status === "pending" || i.status === "active");
    const pastEmail = emailInvites.filter((i) => i.status !== "pending" && i.status !== "active");
    const activeLinks = linkInvites.filter((i) => i.status === "active");
    const inactiveLinks = linkInvites.filter((i) => i.status !== "active");

    return (
        <div className="pm-fade" data-testid="venue-team-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Team</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Venue team</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
                        <DialogTrigger asChild><button className="pm-btn" data-testid="venue-share-link-btn"><Link2 className="w-4 h-4 mr-2" /> Create invite link</button></DialogTrigger>
                        <ShareLinkDialog venueId={venue.id} onDone={() => { setLinkOpen(false); load(); }} />
                    </Dialog>
                    <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
                        <DialogTrigger asChild><button className="pm-btn pm-btn-primary" data-testid="venue-invite-email-btn"><Plus className="w-4 h-4 mr-2" /> Invite by email</button></DialogTrigger>
                        <EmailInviteDialog venueId={venue.id} onDone={() => { setEmailOpen(false); load(); }} />
                    </Dialog>
                </div>
            </header>

            <div className="px-4 md:px-8 py-6 space-y-8">
                <section>
                    <div className="text-[10px] font-mono uppercase text-[#666] mb-2">Members ({team.length})</div>
                    <div className="border border-[#2a2a2a]">
                        {team.length === 0 ? <div className="p-4 text-sm text-[#a3a3a3]" data-testid="venue-team-empty">No members yet.</div>
                        : team.map((m) => (
                            <div key={m.user_id} className="p-3 border-b border-[#2a2a2a] last:border-b-0 flex items-center gap-3" data-testid={`venue-member-${m.user_id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold uppercase">{m.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666]">{m.email}</div>
                                </div>
                                <div className="text-[10px] font-mono uppercase text-[#ff4400] border border-[#2a2a2a] px-2 py-1">{ROLE_LABEL[m.role]}</div>
                                <button onClick={() => removeMember(m.user_id)} className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]" data-testid={`venue-remove-${m.user_id}`}><X className="w-3 h-3 mr-1" /> Remove</button>
                            </div>
                        ))}
                    </div>
                </section>

                <section>
                    <div className="text-[10px] font-mono uppercase text-[#666] mb-2 flex items-center gap-2"><Link2 className="w-3 h-3" /> Active invite links</div>
                    {summary && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#2a2a2a] border border-[#2a2a2a] mb-3" data-testid="venue-share-summary">
                            <Metric label="Active" value={summary.active_links} testId="venue-share-active" />
                            <Metric label="Unique clicks" value={summary.total_unique_clicks} testId="venue-share-clicks" />
                            <Metric label="Accepted" value={summary.accepted_memberships} testId="venue-share-accepted" />
                            <Metric label="Conversion" value={`${summary.conversion_rate}%`} testId="venue-share-conv" />
                            <Metric label="Best channel" value={summary.best_channel && summary.best_channel.channel !== "unlabeled" ? (CHANNEL_LABEL[summary.best_channel.channel] || summary.best_channel.channel) : "—"} testId="venue-share-best" />
                        </div>
                    )}
                    <div className="border border-[#2a2a2a]">
                        {activeLinks.length === 0 ? <div className="p-4 text-xs text-[#a3a3a3]" data-testid="venue-share-links-empty">No active share links.</div>
                        : activeLinks.map((i) => (
                            <div key={i.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm" data-testid={`venue-share-row-${i.id}`}>
                                <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase truncate" data-testid={`venue-share-role-${i.id}`}>
                                    {ROLE_LABEL[i.role] || i.role}
                                </div>
                                <div className="col-span-6 md:col-span-3 text-[10px] font-mono uppercase text-[#a3a3a3]" data-testid={`venue-share-analytics-${i.id}`}>
                                    <TrendingUp className="w-3 h-3 inline mr-1" /> {i.total_clicks || 0} clicks · {i.unique_clicks || 0} unique · {i.conversion_rate || 0}%
                                </div>
                                <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#ff9500]" data-testid={`venue-share-channel-${i.id}`}>
                                    {i.channel ? (CHANNEL_LABEL[i.channel] || i.channel) : ""}
                                </div>
                                <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#666]" data-testid={`venue-share-last-${i.id}`}>
                                    {i.last_clicked_at ? `last ${i.last_clicked_at.slice(0, 10)}` : `exp ${(i.expires_at || "").slice(0, 10)}`}
                                </div>
                                <div className="col-span-12 md:col-span-3 flex justify-end gap-1 flex-wrap">
                                    <button onClick={() => copyLink(i)} className="pm-btn text-[10px]" data-testid={`venue-share-copy-${i.id}`}><Copy className="w-3 h-3 mr-1" /> Copy link</button>
                                    <button onClick={() => revoke(i)} className="pm-btn text-[10px]" data-testid={`venue-share-revoke-${i.id}`}><X className="w-3 h-3 mr-1" /> Revoke</button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {inactiveLinks.length > 0 && (
                        <details className="mt-2 border border-[#2a2a2a]" data-testid="venue-share-history-toggle">
                            <summary className="cursor-pointer px-3 py-2 text-[10px] font-mono uppercase text-[#666]">Past links ({inactiveLinks.length})</summary>
                            {inactiveLinks.map((i) => (
                                <div key={i.id} className="grid grid-cols-12 gap-3 items-center p-3 border-t border-[#2a2a2a] text-xs opacity-70" data-testid={`venue-share-history-${i.id}`}>
                                    <div className="col-span-6 md:col-span-2 font-mono text-[10px] uppercase">{ROLE_LABEL[i.role] || i.role}</div>
                                    <div className="col-span-6 md:col-span-3 font-mono text-[10px] uppercase text-[#a3a3a3]">
                                        {i.total_clicks || 0} clicks · {i.unique_clicks || 0} unique · {i.conversion_rate || 0}%
                                    </div>
                                    <div className="col-span-6 md:col-span-2 font-mono text-[10px] uppercase text-[#ff9500]">{i.channel ? (CHANNEL_LABEL[i.channel] || i.channel) : ""}</div>
                                    <div className={`col-span-6 md:col-span-2 text-[10px] font-mono uppercase ${STATUS_COLOR[i.status]}`}>{i.status}</div>
                                    <div className="col-span-12 md:col-span-3 text-[10px] font-mono uppercase text-[#666]">
                                        {i.accepted_by_name ? `accepted by ${i.accepted_by_name}` : ""}
                                    </div>
                                </div>
                            ))}
                        </details>
                    )}
                </section>

                <section>
                    <div className="text-[10px] font-mono uppercase text-[#666] mb-2 flex items-center gap-2"><Mail className="w-3 h-3" /> Pending email invitations</div>
                    <div className="border border-[#2a2a2a]">
                        {pendingEmail.length === 0 ? <div className="p-4 text-xs text-[#a3a3a3]" data-testid="venue-invites-empty">No pending invitations.</div>
                        : pendingEmail.map((i) => (
                            <div key={i.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm" data-testid={`venue-invite-${i.id}`}>
                                <div className="col-span-12 md:col-span-5 font-mono text-xs truncate">{i.email}</div>
                                <div className="col-span-3 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]">{ROLE_LABEL[i.role]}</div>
                                <div className="col-span-3 md:col-span-2 text-[10px] font-mono uppercase text-[#666]">exp {(i.expires_at || "").slice(0, 10)}</div>
                                <div className="col-span-12 md:col-span-3 flex justify-end gap-1 flex-wrap">
                                    <button onClick={() => copyLink(i)} className="pm-btn text-[10px]" data-testid={`venue-invite-copy-${i.id}`}><Copy className="w-3 h-3 mr-1" /> Link</button>
                                    <button onClick={() => revoke(i)} className="pm-btn text-[10px]"><X className="w-3 h-3 mr-1" /> Revoke</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {pastEmail.length > 0 && (
                    <section>
                        <div className="text-[10px] font-mono uppercase text-[#666] mb-2">Email history</div>
                        <div className="border border-[#2a2a2a]">
                            {pastEmail.map((i) => (
                                <div key={i.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-xs opacity-70" data-testid={`venue-invite-history-${i.id}`}>
                                    <div className="col-span-12 md:col-span-5 truncate font-mono">{i.email}</div>
                                    <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]">{ROLE_LABEL[i.role]}</div>
                                    <div className="col-span-6 md:col-span-5 text-[10px] font-mono uppercase text-[#a3a3a3]">{i.accepted_by_name ? `accepted by ${i.accepted_by_name}` : i.status}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

function Metric({ label, value, testId }) {
    return (
        <div className="bg-[#0a0a0a] p-3" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            <div className="mt-1 font-display font-bold text-lg truncate">{value}</div>
        </div>
    );
}

function EmailInviteDialog({ venueId, onDone }) {
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("staff");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!email) { toast.error("Email required"); return; }
        setBusy(true);
        try {
            const r = await endpoints.venueCreateInvite(venueId, { email, role });
            toast.success(r.email_sent ? "Invite sent" : "Invite saved; delivery unavailable");
            if (r.dev_invite_link) toast(`Dev link: ${r.dev_invite_link}`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Invite by email</DialogTitle></DialogHeader>
            <div className="space-y-3">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="venue-invite-email" />
                <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs" data-testid="venue-invite-role">
                    <option value="staff" className="bg-[#141414]">Staff</option>
                    <option value="manager" className="bg-[#141414]">Manager</option>
                </select>
            </div>
            <DialogFooter><button onClick={submit} disabled={busy} className="pm-btn pm-btn-primary" data-testid="venue-invite-submit"><Send className="w-4 h-4 mr-2" /> Send</button></DialogFooter>
        </DialogContent>
    );
}

function ShareLinkDialog({ venueId, onDone }) {
    const [role, setRole] = useState("staff");
    const [expiresHours, setExpiresHours] = useState(72);
    const [channel, setChannel] = useState("");
    const [created, setCreated] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            const payload = { role, expires_in_hours: expiresHours };
            if (channel) payload.channel = channel;
            const r = await endpoints.venueShareLink(venueId, payload);
            const url = `${window.location.origin}/accept-invite?token=${r.token}`;
            setCreated({ ...r, url });
            toast.success("Link created");
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const copy = () => { navigator.clipboard.writeText(created.url); toast.success("Copied"); };
    const close = () => { setCreated(null); setRole("staff"); setExpiresHours(72); setChannel(""); onDone(); };
    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Create share link</DialogTitle></DialogHeader>
            {!created ? (<>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Role</span>
                        <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full mt-1 bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs" data-testid="venue-share-role">
                            <option value="staff" className="bg-[#141414]">Staff (event check-in)</option>
                            <option value="manager" className="bg-[#141414]">Manager</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Expires in</span>
                        <select value={expiresHours} onChange={(e) => setExpiresHours(Number(e.target.value))} className="w-full mt-1 bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs" data-testid="venue-share-expiry">
                            {EXPIRY.map((o) => <option key={o.value} value={o.value} className="bg-[#141414]">{o.label}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Channel (optional)</span>
                        <select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full mt-1 bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs" data-testid="venue-share-channel-select">
                            {CHANNELS.map((c) => <option key={c.value} value={c.value} className="bg-[#141414]">{c.label}</option>)}
                        </select>
                    </label>
                </div>
                <DialogFooter><button onClick={submit} disabled={busy} className="pm-btn pm-btn-primary" data-testid="venue-share-generate">Generate</button></DialogFooter>
            </>) : (<>
                <div className="space-y-2">
                    <div className="border border-[#2a2a2a] p-3 bg-[#141414] font-mono text-xs break-all" data-testid="venue-share-url">{created.url}</div>
                </div>
                <DialogFooter>
                    <button onClick={copy} className="pm-btn pm-btn-primary" data-testid="venue-share-copy"><Copy className="w-4 h-4 mr-2" /> Copy</button>
                    <button onClick={close} className="pm-btn" data-testid="venue-share-done">Done</button>
                </DialogFooter>
            </>)}
        </DialogContent>
    );
}
